import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarMenu, SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, cn, useSidebar as useSidebarProvider } from '@appic/ui-components'
import React, { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { NavItem, flatNavigationConfig } from '@/config/nav'
import { useLanguage } from '@/contexts/LanguageContext'
import { getIconComponent } from '@/lib/iconMapper'
import { LayoutDashboard, ChevronRight } from 'lucide-react'
import { usePermissions } from '@appic/hooks'
interface SidebarNavProps {
  className?: string
}

export const SidebarNav: React.FC<SidebarNavProps> = ({ className }) => {
  const location = useLocation()
  const { t } = useLanguage()
  const { open, setOpen, openMobile, setOpenMobile, isMobile } = useSidebarProvider()
  const [hoverItemId, setHoverItemId] = useState<string | null>(null)
  // Use mobile state on mobile, desktop state otherwise
  const sidebarOpen = isMobile ? openMobile : open
  const setSidebarOpen = isMobile ? setOpenMobile : setOpen
  const isCollapsed = !sidebarOpen

  const { canAccess, permissionMap } = usePermissions()
  const filteredNavItems = useMemo(() => {
    return flatNavigationConfig
      .filter(item => !item.permission || canAccess(item.permission))
      .map(item => {
        // Filter feature-level tabs by permission when permission map is available
        if (permissionMap.length > 0 && item.tabItems && item.tabItems.length > 0) {
          const filteredTabs = item.tabItems.filter(tab =>
            !tab.permission || canAccess(tab.permission)
          )
          return { ...item, tabItems: filteredTabs }
        }
        return item
      })
  }, [canAccess, permissionMap])

  const isActive = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }

  const NavItemComponent: React.FC<{ item: NavItem; level?: number }> = ({ item, level = 0 }) => {
    const Icon = getIconComponent(item.icon, LayoutDashboard)
    const isItemActive = isActive(item.href)
    const itemChildren = (item.children && item.children.length > 0)
      ? item.children
      : (item.tabItems && item.tabItems.length > 0 ? item.tabItems : undefined)
    const hasChildren = !!itemChildren && itemChildren.length > 0
    const isChildActive = hasChildren && itemChildren?.some(child => isActive(child.href))
    const [isExpanded, setIsExpanded] = useState(false)
    
    if (level > 0) {
      // Nested items use SidebarMenuSubButton
      return (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton
            asChild
            isActive={isItemActive}
            data-sidebar-sub-button
          >
            <Link 
              to={item.href}
              className="flex w-full items-start gap-2 min-w-0"
            >
              <Icon className="shrink-0" />
              <span data-sidebar-text className="break-words whitespace-normal flex-1 min-w-0">{t(item.labelKey)}</span>
              {item.badge !== undefined && item.badge !== null && (
                <SidebarMenuBadge className="shrink-0">{item.badge}</SidebarMenuBadge>
              )}
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      )
    }

    // Top-level items
    return (
      <SidebarMenuItem
        onMouseEnter={() => !isMobile && hasChildren && setHoverItemId(item.id)}
        onMouseLeave={() => !isMobile && hasChildren && setHoverItemId((current) => (current === item.id ? null : current))}
      >
        <SidebarMenuButton
          asChild
          isActive={isItemActive || !!isChildActive}
          tooltip={isCollapsed ? t(item.labelKey) : undefined}
          data-sidebar-menu-button
        >
          <Link 
            to={item.href} 
            onClick={(e) => {
              if (hasChildren) {
                e.preventDefault()
                setIsExpanded(!isExpanded)
              }
            }}
            className="flex w-full items-start gap-2 min-w-0"
          >
            <Icon className="shrink-0" />
            <span data-sidebar-text className="break-words whitespace-normal flex-1 min-w-0">{t(item.labelKey)}</span>
            {item.badge !== undefined && item.badge !== null && (
              <SidebarMenuBadge data-sidebar-badge className="shrink-0">{item.badge}</SidebarMenuBadge>
            )}
            {hasChildren && (
              <ChevronRight className={cn(
                "ml-auto shrink-0 transition-transform duration-200",
                isExpanded && "rotate-90"
              )} />
            )}
          </Link>
        </SidebarMenuButton>
        {hasChildren && (isExpanded || (!isMobile && hoverItemId === item.id)) && (
          <SidebarMenuSub>
            {itemChildren?.map((child) => (
              <NavItemComponent key={child.id} item={child} level={level + 1} />
            ))}
          </SidebarMenuSub>
        )}
        {hasChildren && !isMobile && hoverItemId === item.id && (
          <div className="absolute left-full top-0 ml-1 min-w-48 w-max max-w-xs rounded-md bg-sidebar shadow-lg py-1 px-2 z-50">
            {itemChildren?.map((child) => (
              <NavItemComponent key={`flyout-${child.id}`} item={child} level={level + 1} />
            ))}
          </div>
        )}
      </SidebarMenuItem>
    )
  }

  return (
    <Sidebar
      collapsible="offcanvas"
      open={sidebarOpen}
      onOpenChange={setSidebarOpen}
      className={className}
    >
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredNavItems.map((item) => (
                <NavItemComponent key={item.id} item={item} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="border-t border-border px-4 py-2" />
    </Sidebar>
  )
}